import React, {useState, useEffect, useRef} from 'react';
import {Button, Form, Input, Select, Modal, Row, Col, Space, Tag, Checkbox} from 'antd';
import {DownOutlined, EditOutlined, SearchOutlined, UpOutlined} from '@ant-design/icons';
import Table from "@/biz/Table.jsx";
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from '@/script/runAsync';
import BizNodeTypeTag, {
    convertToBizNodeTypeName,
    convertToDppSubTypeName,
    bizNodeTypeOptions
} from "@/components/BizNodeTypeTag/BizNodeTypeTag";
import buildUrl from '@/script/buildUrl.mjs';
import {openDetail} from '@/script/DpUtils.mjs';

/**
 * SelectPatternModuleDialog ：选择模型模件
 * @param onOk
 * @param onCancel
 * @param isSingle
 * @param prjPatternId
 * @param filterBizNodeTypes
 * @param title
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const SelectPatternModuleDialog = ({
                                visible,
                                onOk,
                                onCancel,
                                isSingle = true,
                                prjPatternId,
                                filterBizNodeTypes,
                                title = "选择模型模件",
                                ...props
                            }) => {
    const http = useHTTP();

    // 表单
    const [form] = Form.useForm();

    // 数据模型
    const [pattern, setPattern] = useState({});
    const [data, setData] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);
    const [expand, setExpand] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]); // 选中的对象集合

    // 组件初始化
    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                bizNodeTypes: filterBizNodeTypes,
            });
            setData([]);
            setSelectedRows([]); // 处理被选中的项目，当主Dialog关闭，需要清空选择的数据
            const _pattern = queryPattern();
            doSearch(_pattern?.subTypeCode ?? 'BODA2');
        }
    }, [visible]);

    // 初始化权限
    const queryPattern = async () => {
        try {
            const data = await runAsync(
                async () => {
                    return await http.get(`/prjpatterns/${prjPatternId}`);
                }, {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: false },
                    success: { show: false },
                });
            setPattern(data);
            return data;
        } catch (err) {
            console.log(err);
        }
        return null;
    }

    const doSearch = async (subTypeCode) => {
        try {
            setLoading(true);
            const formField = await form.getFieldsValue();
            formField.prjPatternId = prjPatternId;
            if (formField.bizNodeTypes && formField.bizNodeTypes.length > 0) {
                const bizNodeTypes = new Set();
                for (let i = 0; i < formField.bizNodeTypes.length; i++) {
                    let bizNodeType = formField.bizNodeTypes[i];
                    if (bizNodeType) {
                        const types = bizNodeType.split(/,+/).filter(Boolean);
                        types.forEach(type => bizNodeTypes.add(type));
                    }
                }
                formField.bizNodeTypes = Array.from(bizNodeTypes);
            } else if(filterBizNodeTypes && filterBizNodeTypes.length > 0 ){
                formField.bizNodeTypes = filterBizNodeTypes;
            }
            const data = await runAsync(
                async () => {
                    return await http.get(`/prjpatterns/dpp`, { ...formField });
                }, {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: true },
                    success: { show: false },
                });

            if (data) {
                data.forEach(item => {
                    // 将制品版本显示到模型版本
                    if (item.dpFlag === '1') {
                        item.dppRev = item.dpRev
                    }

                    item.dpTypeName = convertToBizNodeTypeName(item.bizNodeType, item.dpType, subTypeCode)

                    if (item.dpFlag == '1') {
                        item.dpSnShow = item.dpSn;

                        item.dppNameShow = item.dpName + (item.dpNo ? '/' + item.dpNo : '');

                        item.dpNameShow = null;
                        item.dpRevShow = null;
                    } else {
                        item.dpSnShow = item.dppSn;
                        item.dppNameShow = '【' + item.dpName + '-V' + item.sVer + '.' + item.dpRev + '】' + item.dppName + (item.dppNo ? '/' + item.dppNo : '');

                        item.dpNameShow = item.dpName;
                        item.dpRevShow = item.dpRev;
                    }

                    item.dppRevShow = item.sVer + "." + item.dppRev;

                    item.subTypeCode = subTypeCode;
                });
            }
            setData(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const doReset = async () => {
        form.resetFields();
        doSearch();
        setSelectedRows([]);
    }

    // 确认回调
    const doConfirm = async () => {
        if (isSingle) {
            if (selectedRows.length == 0) {
                onOk(null);
            } else {
                const data = selectedRows[0];
                onOk(data);
            }
        } else {
            onOk(selectedRows);
        }
    }

    const handleTableRowClick = (row) => {
        if(isSingle){
            onOk(row)
        } else {
            onOk([row])
        }
    }

    // 关闭Dialog
    const doClose = () => {
        form.resetFields();
        setSelectedRows([]);
        onCancel();
    }

    const rowSelection = {
        type: isSingle ? 'radio' : 'checkbox',
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    const columns = [
        {
            title: '模件类型',
            dataIndex: 'bizNodeTypeName',
            width: 160,
            ellipsis: true,
            getValue: record => convertToBizNodeTypeName(record.bizNodeType, record.dpType, record.subTypeCode),
            filter: <Table.FilterText />,
            render: (text, record) => {
                return <BizNodeTypeTag bizNodeType={record.bizNodeType} dpType={record.dpType} subTypeCode={record.subTypeCode}/>
            }
        },
        {
            title: '模件域',
            dataIndex: 'moduleDomainName',
            width: 160,
            ellipsis: true,
            filter: <Table.FilterText />,
        },
        {
            title: '模件编号',
            dataIndex: 'dpSnShow',
            width: 160,
            ellipsis: true,
            filter: <Table.FilterText />,
        },
        {
            title: '模件名称/代码',
            dataIndex: 'dppNameShow',
            //width: 150,
            ellipsis: true,
            filter: <Table.FilterText />,
            render: (text, record, index) => {

                const openVersion = (record) => {
                    let url;
                    if (record.dpFlag == '1') {
                        url = buildUrl('/SoftwareMap', { id: record.mapVerId, mode: 'snapshot', prjPatternId, patternRev });
                    } else {
                        const inits = {
                            selectedNodes: [{
                                data: { pkid: record.dppId },
                                isMounted: true,
                            }]
                        };
                        url = buildUrl('/SoftwareMap', { id: record.mapVerId, mode: 'snapshot', inits, prjPatternId, patternRev });
                    }
                    window.open(url);
                }

                // 制品、集成图
                if (record.dpFlag !== '0') {
                    const bizNodeType = record.dpBizNodeType == 'BD' ? (record.dpType ?? record.dpBizNodeType) : record.dpBizNodeType;
                    return (
                        <Space>
                            <a href="javascript:void(0);" rel="noreferrer" onClick={() => openVersion(record)}>{text}</a>
                            &nbsp;
                            <a href="javascript:void(0);" rel="noreferrer" onClick={() => openDetail(record.dpId, bizNodeType, null, null, record.prjId)}>
                                <div style={{ backgroundColor: '#e9f7fe', width: '15px' }}>
                                    <EditOutlined />
                                </div>
                            </a>
                        </Space>
                    );
                } else {
                    const bizNodeType = record.dpBizNodeType == 'BD' ? (record.dpType ?? record.dpBizNodeType) : record.dpBizNodeType;

                    return (
                        <Space>
                            <a href="javascript:void(0);" rel="noreferrer" onClick={() => openVersion(record)}>{text}</a>
                            &nbsp;
                            <a href="javascript:void(0);" rel="noreferrer" onClick={() => openDetail(record.dpId, bizNodeType, record.dppId, true)}>
                                <div style={{ backgroundColor: '#e9f7fe', width: '15px' }}>
                                    <EditOutlined />
                                </div>
                            </a>
                        </Space>
                    );
                }
            }
        },
        {
            title: '模件版本号',
            dataIndex: 'dppRevShow',
            width: 160,
            ellipsis: true,
            filter: <Table.FilterText />,
        },
        {
            title: '操作',
            dataIndex: 'operation',
            width: 100,
            className: 'center',
            render: (text, record) => {
                return (
                    <a onClick={() => handleTableRowClick(record)}>
                        选择
                    </a>
                )
            }
        },
    ];

    const formCss = {
        '& .ant-form-item': {
            marginBottom: '12px'
        }
    }

    const dpTypeSearch = (inputValue, option) => {
        return option?.children?.toLowerCase().indexOf(inputValue?.toLowerCase()) >= 0 ? true : false;
    }

    return (
        <Modal
            open={visible}
            title={title}
            width={1500}
            onOk={doConfirm}
            onCancel={doClose}
            bodyStyle={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '70vh',
                minHeight: 320,
                background: '#fff',
                padding: 12
            }}
        >
            <div style={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                padding: 0,
                gap: 12,
                flexGrow: 1
            }}>
                {/* 查询条件 */}
                <div style={{padding: '0px 12px', background: '#fff'}}>
                    <Form form={form} onFinish={() => doSearch()}>
                        <Row gutter={24}>
                            <Col span={4}>
                                <Form.Item name="bizNodeTypes" label="模件类型">
                                    <Select mode="multiple" showSearch={true} allowClear={true}
                                            filterOption={(inputValue, option) => dpTypeSearch(inputValue, option)}
                                    >
                                        {
                                            bizNodeTypeOptions(pattern?.subTypeCode ?? 'BODA2').filter(item => item.dpFlag !== '2' && item.prjTypeCodes.includes(pattern?.prjTypeCode)).map(({
                                                                                                                                                                                                 label,
                                                                                                                                                                                                 value
                                                                                                                                                                                             }) => {
                                                return (
                                                    <Select.Option value={value} label={label}>
                                                        {label}
                                                    </Select.Option>
                                                )
                                            })
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="dpSn" label="模件编号">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="dppNo" label="模件代码">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="dppName" label="模件名称">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="moduleDomainName" label="模件域">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>
                            <Col span={4} style={{ display: expand ? 'block' : 'none' }}>
                                <Form.Item name="dpName" label="所属制品">
                                    <Input allowClear/>
                                </Form.Item>
                            </Col>

                            <Col span={4} offset={expand ? 20 : 0} style={{textAlign: 'right'}}>
                                <Button onClick={doReset} style={{margin: '0 8px'}}>重置</Button>
                                <Button type="primary" htmlType="submit" icon={<SearchOutlined/>}>查询</Button>

                                <a
                                    style={{fontSize: 12, marginLeft: '8px'}}
                                    onClick={() => {
                                        setExpand(!expand);
                                    }}
                                >
                                    {expand ? <UpOutlined/> : <DownOutlined/>} {expand ? '收起' : '展开'}
                                </a>
                            </Col>
                        </Row>
                    </Form>
                </div>

                {/* 数据表格 */}
                <div style={{
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fff',
                    padding: '12px',
                    flexGrow: 1,
                    marginTop: '6px'
                }}>
                    <Table
                        size="middle"
                        loading={loading}
                        columns={columns}
                        dataSource={data}
                        rowKey={"pkid"}
                        rowSelection={{type: "checkbox", ...rowSelection}}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default SelectPatternModuleDialog;